interface DataPoint {
  x: Date;
  y: number;
}

/** Lower bound on the missing-data gap threshold (used when the inferred sample cadence is faster than this). */
export const MISSING_DATA_GAP_MS = 60 * 60 * 1000; // 1 hour
/** Multiplier applied to the inferred sample cadence to decide what counts as a gap. */
export const MISSING_DATA_GAP_MULTIPLIER = 3;
/** Red used for the dashed line drawn across a missing-data gap. */
export const MISSING_DATA_COLOR = 'rgba(239, 68, 68, 0.85)';
/** Dash pattern for missing-data segments. */
export const MISSING_DATA_DASH: [number, number] = [6, 4];

/**
 * Picks an appropriate "missing-data" threshold for the supplied series.
 * Bucketed/aggregated server responses (e.g. 7-day view → 2-hour buckets) would
 * otherwise look entirely missing if we used a fixed 1-hour threshold, since
 * every consecutive segment is > 1 hour apart by design. We infer the typical
 * cadence from the median delta between consecutive points and only flag gaps
 * that are notably larger than that — floored at MISSING_DATA_GAP_MS so raw
 * fast-sample series still flag anything over an hour.
 */
export function inferGapThresholdMs(points: DataPoint[]): number {
  if (points.length < 3) return MISSING_DATA_GAP_MS;
  const deltas: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const d = points[i].x.getTime() - points[i - 1].x.getTime();
    if (Number.isFinite(d) && d > 0) deltas.push(d);
  }
  if (deltas.length === 0) return MISSING_DATA_GAP_MS;
  deltas.sort((a, b) => a - b);
  const median = deltas[Math.floor(deltas.length / 2)];
  return Math.max(MISSING_DATA_GAP_MS, median * MISSING_DATA_GAP_MULTIPLIER);
}

/**
 * Chart.js `pointRadius` callback: only draws individual point markers when they're
 * sparse on screen (deep zoom / sparse data). Stops the temperature line from
 * appearing to vanish at high zoom levels where the visible window contains a
 * single point — the dot itself becomes visible instead.
 */
export function adaptivePointRadius(ctx: any): number {
  const data = (ctx?.dataset?.data ?? []) as Array<{ x?: Date | number }>;
  if (data.length === 0) return 0;
  const xAxis = ctx?.chart?.scales?.x;
  const xMin = typeof xAxis?.min === 'number' ? xAxis.min : undefined;
  const xMax = typeof xAxis?.max === 'number' ? xAxis.max : undefined;
  const widthPx = typeof xAxis?.width === 'number' ? xAxis.width : 0;
  if (xMin == null || xMax == null || widthPx <= 0) {
    return data.length === 1 ? 3 : 0;
  }
  let inView = 0;
  for (const p of data) {
    const raw = p?.x;
    const x = raw instanceof Date ? raw.getTime() : Number(raw);
    if (!Number.isFinite(x)) continue;
    if (x >= xMin && x <= xMax) inView++;
  }
  if (inView === 0) return 0;
  if (inView === 1) return 3;
  const pxPerPoint = widthPx / inView;
  return pxPerPoint > 30 ? 3 : 0;
}

/**
 * Builds a Chart.js `segment` config that paints any line segment whose endpoints
 * span more than `thresholdMs` in red dashed, so true missing-data gaps stand out
 * from the smoothly-connected baseline. The threshold is supplied per series
 * because bucketed data has a much larger normal cadence than raw readings.
 */
export function makeGapSegmentStyle(thresholdMs: number) {
  return {
    borderColor: (ctx: any): string | undefined => {
      const p0x = ctx?.p0?.parsed?.x;
      const p1x = ctx?.p1?.parsed?.x;
      if (typeof p0x !== 'number' || typeof p1x !== 'number') return undefined;
      return p1x - p0x > thresholdMs ? MISSING_DATA_COLOR : undefined;
    },
    borderDash: (ctx: any): number[] | undefined => {
      const p0x = ctx?.p0?.parsed?.x;
      const p1x = ctx?.p1?.parsed?.x;
      if (typeof p0x !== 'number' || typeof p1x !== 'number') return undefined;
      return p1x - p0x > thresholdMs ? [...MISSING_DATA_DASH] : undefined;
    },
  };
}

/**
 * Returns one marker per missing-data gap at the gap midpoint, so users can hover
 * the gap and see how long readings were missing for. `gapMs` is preserved on each
 * marker so a tooltip callback can format it. `thresholdMs` defaults to the global
 * 1-hour minimum but should usually be the inferred per-series value.
 */
export function buildGapMarkers(
  points: DataPoint[],
  thresholdMs: number = MISSING_DATA_GAP_MS
): Array<{ x: Date; y: number; gapMs: number }> {
  const out: Array<{ x: Date; y: number; gapMs: number }> = [];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const gap = curr.x.getTime() - prev.x.getTime();
    if (gap > thresholdMs) {
      out.push({
        x: new Date(prev.x.getTime() + gap / 2),
        y: (prev.y + curr.y) / 2,
        gapMs: gap,
      });
    }
  }
  return out;
}

/** Human-friendly duration string for missing-data tooltips. */
export function formatGapDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0 minutes';
  const minutes = ms / 60000;
  if (minutes < 90) return `${Math.round(minutes)} minutes`;
  const hours = minutes / 60;
  if (hours < 36) return `${hours.toFixed(1)} hours`;
  const days = hours / 24;
  if (days < 14) return `${days.toFixed(1)} days`;
  return `${(days / 7).toFixed(1)} weeks`;
}

/** Distance from v to nearest 0.1 °C grid (in °C). */
function distanceToTenthGrid(v: number): number {
  const x = v * 10;
  return Math.abs(x - Math.round(x)) / 10;
}

/**
 * True when most readings sit on a 0.1 °C grid (e.g. Hanwell EMS / 1 decimal sensors),
 * so the time series looks "blockyy" if plotted raw.
 */
export function isLikelyOneDecimalTemperatures(values: number[]): boolean {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length < 5) return false;
  let onGrid = 0;
  for (const v of finite) {
    if (distanceToTenthGrid(v) < 0.02) onGrid++;
  }
  return onGrid / finite.length >= 0.88;
}

function mergeDuplicateTimestamps(sorted: DataPoint[]): DataPoint[] {
  if (sorted.length === 0) return sorted;
  const out: DataPoint[] = [{ x: sorted[0].x, y: sorted[0].y }];
  for (let i = 1; i < sorted.length; i++) {
    const p = sorted[i];
    const last = out[out.length - 1];
    if (p.x.getTime() === last.x.getTime()) {
      out[out.length - 1] = { x: last.x, y: (last.y + p.y) / 2 };
    } else {
      out.push({ x: p.x, y: p.y });
    }
  }
  return out;
}

function dedupeByMinTimeGap(sorted: DataPoint[], minGapMs: number): DataPoint[] {
  const deduped: DataPoint[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0 || sorted[i].x.getTime() - deduped[deduped.length - 1].x.getTime() >= minGapMs) {
      deduped.push(sorted[i]);
    }
  }
  return deduped;
}

/**
 * Triangular weighted moving average (centered where possible).
 * Smooths plateaus from low-resolution sensors without shifting times.
 */
function triangularMovingAverage(points: DataPoint[], halfWindow: number): DataPoint[] {
  if (points.length === 0) return points;
  const n = points.length;
  const out: DataPoint[] = [];
  for (let i = 0; i < n; i++) {
    let sumY = 0;
    let sumW = 0;
    for (let j = -halfWindow; j <= halfWindow; j++) {
      const idx = i + j;
      if (idx < 0 || idx >= n) continue;
      const w = halfWindow + 1 - Math.abs(j);
      sumY += points[idx].y * w;
      sumW += w;
    }
    const y = sumW > 0 ? sumY / sumW : points[i].y;
    out.push({ x: points[i].x, y: Number(y.toFixed(3)) });
  }
  return out;
}

/** Spike reduction / interpolation (legacy path for higher-resolution sensors). */
function applySpikeSmoothing(deduped: DataPoint[]): DataPoint[] {
  const smoothed: DataPoint[] = [];
  const SPIKE_THRESHOLD = 0.2;
  const WINDOW_SIZE = 2;

  smoothed.push(deduped[0]);
  if (deduped.length > 1) smoothed.push(deduped[1]);

  for (let i = 2; i < deduped.length - 2; i++) {
    const current = deduped[i].y;

    const prevPoints = deduped.slice(Math.max(0, i - WINDOW_SIZE), i).map((p) => p.y);
    const nextPoints = deduped
      .slice(i + 1, Math.min(deduped.length, i + WINDOW_SIZE + 1))
      .map((p) => p.y);

    const surroundingAvg =
      [...prevPoints, ...nextPoints].reduce((a, b) => a + b, 0) /
      (prevPoints.length + nextPoints.length);

    const trend = (nextPoints[0] - prevPoints[0]) / (WINDOW_SIZE * 2);
    const expectedValue = surroundingAvg + trend;

    if (Math.abs(current - expectedValue) > SPIKE_THRESHOLD) {
      const prevTime = deduped[i - 1].x.getTime();
      const nextTime = deduped[i + 1].x.getTime();
      const currentTime = deduped[i].x.getTime();
      const weight = (currentTime - prevTime) / (nextTime - prevTime);
      const prevValue = prevPoints[prevPoints.length - 1];
      const nextValue = nextPoints[0];
      const interpolatedValue = prevValue + (nextValue - prevValue) * weight + trend;

      smoothed.push({
        x: deduped[i].x,
        y: Number(interpolatedValue.toFixed(2)),
      });
    } else {
      smoothed.push({
        x: deduped[i].x,
        y: Number(current.toFixed(2)),
      });
    }
  }

  if (deduped.length > 3) smoothed.push(deduped[deduped.length - 2]);
  smoothed.push(deduped[deduped.length - 1]);

  return smoothed;
}

/**
 * Smooths temperature data: spike handling for dense series, triangular averaging
 * for ~0.1 °C–quantized (EMS-style) data so charts are less blocky.
 */
export function smoothTemperatureData(data: DataPoint[]): DataPoint[] {
  if (data.length === 0) return data;

  const sorted = [...data].sort((a, b) => a.x.getTime() - b.x.getTime());
  const coarse = isLikelyOneDecimalTemperatures(sorted.map((p) => p.y));

  // Low-res sensors: keep every sample (only merge identical timestamps).
  // Others: legacy 10 s dedupe to reduce noise on fast-sampling probes.
  const series = coarse
    ? mergeDuplicateTimestamps(sorted)
    : dedupeByMinTimeGap(sorted, 10000);

  let result: DataPoint[];
  if (series.length >= 5) {
    result = applySpikeSmoothing(series);
  } else {
    result = series.map((p) => ({ x: p.x, y: Number(p.y.toFixed(2)) }));
  }

  if (coarse && result.length >= 3) {
    result = triangularMovingAverage(result, 2);
  }

  return result;
}

/**
 * Creates a smooth battery voltage curve by using a large window moving average
 * and additional smoothing techniques to eliminate oscillations
 */
export function smoothBatteryData(data: DataPoint[]): DataPoint[] {
  if (data.length < 3) return data;

  // First, ensure data is sorted chronologically
  const sortedData = [...data].sort((a, b) => a.x.getTime() - b.x.getTime());

  const smoothed: DataPoint[] = [];
  const WINDOW_SIZE = 30; // Large window for very smooth transitions

  // Add first point as is
  smoothed.push(sortedData[0]);

  for (let i = 1; i < sortedData.length; i++) {
    // Get window of points, ensuring we don't exceed array bounds
    const windowStart = Math.max(0, i - WINDOW_SIZE + 1);
    const window = sortedData.slice(windowStart, i + 1);

    // Calculate weighted moving average with linear weights
    // More recent points get higher weights
    let weightedSum = 0;
    let weightSum = 0;

    for (let j = 0; j < window.length; j++) {
      const weight = j + 1; // Linear weight increasing with recency
      weightedSum += window[j].y * weight;
      weightSum += weight;
    }

    const smoothedValue = weightedSum / weightSum;

    // Additional smoothing: if we have a previous point, limit the rate of change
    if (smoothed.length > 0) {
      const prevValue = smoothed[smoothed.length - 1].y;
      const maxChange = 0.001; // Maximum allowed change between consecutive points
      const change = smoothedValue - prevValue;
      const limitedChange = Math.max(Math.min(change, maxChange), -maxChange);

      smoothed.push({
        x: sortedData[i].x,
        y: Number((prevValue + limitedChange).toFixed(3)),
      });
    } else {
      smoothed.push({
        x: sortedData[i].x,
        y: Number(smoothedValue.toFixed(3)),
      });
    }
  }

  return smoothed;
}
